import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, TrendingUp, TrendingDown, Receipt, Plus, Trash2, Loader2, RefreshCw,
  Download, CalendarDays, Info, FileSpreadsheet, Send,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import AdminNav from "@/components/AdminNav";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(Number(n || 0));
const monthLabel = (p) => { try { const [y, m] = p.split("-"); return new Date(Number(y), Number(m) - 1).toLocaleDateString("es-CL", { month: "long", year: "numeric" }); } catch { return p; } };
const currentPeriod = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const periodOptions = () => {
  const out = []; const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const CAT_LABEL = {
  servicios_profesionales: "Servicios profesionales",
  software_licencias: "Software / licencias",
  hosting_cloud: "Hosting / cloud",
  marketing: "Marketing",
  gastos_bancarios: "Gastos bancarios",
  impuestos: "Impuestos",
  arriendo: "Arriendo",
  comisiones_pasarela: "Comisiones pasarela",
  otro: "Otro",
};

export default function AdminAccounting() {
  const [period, setPeriod] = useState(currentPeriod());
  const [summary, setSummary] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payouts, setPayouts] = useState({ items: [], total_clp: 0, by_professional: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [s, inc, exp, pay] = await Promise.all([
        api.get(`/admin/accounting/summary?period=${period}`),
        api.get(`/admin/accounting/income?period=${period}`),
        api.get(`/admin/accounting/expenses?period=${period}`),
        api.get(`/admin/accounting/payouts?period=${period}`),
      ]);
      setSummary(s.data);
      setIncome(inc.data.items || []);
      setExpenses(exp.data.items || []);
      setPayouts(pay.data || { items: [], total_clp: 0, by_professional: [], count: 0 });
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]);   // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (eid) => {
    if (!window.confirm("¿Eliminar este gasto? La acción queda registrada en el log de auditoría.")) return;
    try { await api.delete(`/admin/accounting/expenses/${eid}`); load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Descarga los CSVs oficiales del SII (compras / ventas) para el período seleccionado.
  const downloadLibroSII = async (kind) => {
    setError("");
    try {
      const resp = await api.get(`/admin/accounting/libro-iva/${kind}?period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `libro-${kind}-sii-${period}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="container-app py-8" data-testid="admin-accounting-page">
      <AdminNav />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow text-emerald-300">Administración</div>
          <h1 className="serif text-4xl mt-1 inline-flex items-center gap-2"><Wallet size={26} className="text-emerald-300"/> Contabilidad</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Ingresos automáticos (comisiones y % de acuerdos), gastos manuales, IVA y retenciones estimadas para tu declaración mensual.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarDays size={12}/>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="field py-1 text-xs" data-testid="accounting-period">
              {periodOptions().map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
            </select>
          </div>
          <button onClick={load} className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5"><RefreshCw size={12}/> Refrescar</button>
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{error}</div>}

      {/* Métricas */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Ingresos del mes" value={CLP(summary?.income?.total_income_clp)} icon={TrendingUp} accent="emerald" testid="acc-stat-income"/>
        <StatCard label="Gastos brutos" value={CLP(summary?.expenses?.total_gross_clp)} icon={TrendingDown} accent="amber" testid="acc-stat-expenses"/>
        <StatCard label="Utilidad neta" value={CLP(summary?.net_profit_clp)} icon={Wallet} accent={((summary?.net_profit_clp || 0) >= 0) ? "emerald" : "amber"} testid="acc-stat-profit"/>
        <StatCard label="IVA a pagar (est.)" value={CLP(summary?.iva_estimated?.payable_clp)} icon={Receipt} accent="cyan" testid="acc-stat-iva"/>
      </div>

      {/* Panel IVA + retenciones */}
      {summary && (
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div className="panel p-4">
            <div className="text-xs uppercase tracking-wider text-cyan-300 font-medium">IVA (estimado — {(summary.iva_rate * 100).toFixed(0)}%)</div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Débito (ingresos):</span> <strong>{CLP(summary.iva_estimated.debit_from_income_clp)}</strong></div>
              <div><span className="text-muted-foreground">Crédito (gastos):</span> <strong>{CLP(summary.iva_estimated.credit_from_expenses_clp)}</strong></div>
              <div className="col-span-2 pt-1 border-t border-white/5"><span className="text-muted-foreground">A pagar:</span> <strong className="text-cyan-100">{CLP(summary.iva_estimated.payable_clp)}</strong></div>
            </div>
          </div>
          <div className="panel p-4">
            <div className="text-xs uppercase tracking-wider text-amber-300 font-medium">Retenciones honorarios ({(summary.retention_rate * 100).toFixed(2)}%)</div>
            <div className="mt-2 text-sm">
              <span className="text-muted-foreground">Total retenido en boletas registradas:</span> <strong>{CLP(summary.retention_estimated.total_retained_clp)}</strong>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 inline-flex items-start gap-1"><Info size={10} className="mt-0.5"/> {summary.retention_estimated.note}</p>
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
          {summary.disclaimer}
        </div>
      )}

      {/* ═══════════════════ Libro IVA formato SII ═══════════════════ */}
      <div className="mt-6 panel p-5 border-cyan-400/25 bg-cyan-400/[0.03]" data-testid="acc-sii-panel">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-300 font-semibold">
              <FileSpreadsheet size={14}/> Libro IVA formato SII
            </div>
            <h3 className="serif text-xl mt-1">Exportación mensual para tu F29</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Descarga los CSV con las columnas y códigos de documento que espera el <strong>SII</strong> (tipo doc 33=factura, 39=boleta, etc.).
              Sepárador <strong>;</strong> y BOM UTF-8 para abrir directamente en Excel-ES o pegar en el asistente de F29.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => downloadLibroSII("ventas")}
              className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
              data-testid="acc-sii-ventas"
            >
              <Download size={12}/> Libro de Ventas SII
            </button>
            <button
              onClick={() => downloadLibroSII("compras")}
              className="btn-ghost text-xs py-1.5 px-3 border border-cyan-400/40 inline-flex items-center gap-1.5"
              data-testid="acc-sii-compras"
            >
              <Download size={12}/> Libro de Compras SII
            </button>
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-2 text-[11px] text-muted-foreground">
          <div><strong className="text-foreground">Ventas:</strong> comisiones de delegaciones + cargo Colega Link en mediaciones con acuerdo. Se emite como boleta electrónica (código 39) con IVA débito {(summary?.iva_rate ?? 0.19) * 100}% calculado sobre el neto.</div>
          <div><strong className="text-foreground">Compras:</strong> todos los gastos manuales del período con su IVA crédito. Documento tipo 33 para facturas, {'"BH"'} para boletas de honorarios (con retención).</div>
        </div>
      </div>

      {/* Ingresos automáticos */}
      <section className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="serif text-2xl inline-flex items-center gap-2"><TrendingUp size={18} className="text-emerald-300"/> Ingresos automáticos</h2>
          <button
            onClick={() => downloadCSV(
              `ingresos-${period}.csv`, income,
              [
                { key: "at", label: "Fecha", format: (v) => (v ? new Date(v).toISOString() : "") },
                { key: "source", label: "Origen" },
                { key: "detail", label: "Detalle" },
                { key: "reference_id", label: "Referencia" },
                { key: "amount_clp", label: "Monto CLP" },
                { key: "status", label: "Estado" },
              ],
            )}
            disabled={income.length === 0}
            className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="acc-income-export"
          ><Download size={12}/> Exportar CSV</button>
        </div>
        {loading ? <Loader2 size={18} className="mt-4 animate-spin text-muted-foreground"/> : (
          <div className="panel overflow-hidden mt-3">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Origen</th>
                  <th className="text-left px-3 py-2">Detalle</th>
                  <th className="text-right px-3 py-2">Monto</th>
                </tr>
              </thead>
              <tbody>
                {income.map((it, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(it.at).toLocaleDateString("es-CL")}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${it.source === "mediation" ? "border-emerald-400/40 text-emerald-200 bg-emerald-400/5" : "border-cyan-400/40 text-cyan-200 bg-cyan-400/5"}`}>
                        {it.source === "mediation" ? "Mediación" : "Delegación"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">{it.detail}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-200">{CLP(it.amount_clp)}</td>
                  </tr>
                ))}
                {income.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground text-sm">Sin ingresos automáticos en el período.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Gastos manuales */}
      <section className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="serif text-2xl inline-flex items-center gap-2"><TrendingDown size={18} className="text-amber-300"/> Gastos manuales</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadCSV(
                `gastos-${period}.csv`, expenses,
                [
                  { key: "date", label: "Fecha" },
                  { key: "category", label: "Categoría", format: (v) => CAT_LABEL[v] || v },
                  { key: "supplier_name", label: "Proveedor" },
                  { key: "description", label: "Descripción" },
                  { key: "document_kind", label: "Documento" },
                  { key: "document_number", label: "N°" },
                  { key: "amount_net_clp", label: "Neto CLP" },
                  { key: "iva_clp", label: "IVA CLP" },
                  { key: "retention_clp", label: "Retención CLP" },
                  { key: "amount_gross_clp", label: "Bruto CLP" },
                ],
              )}
              disabled={expenses.length === 0}
              className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="acc-expenses-export"
            ><Download size={12}/> Exportar CSV</button>
            <button onClick={() => setFormOpen(true)} className="btn-primary text-xs py-1 px-2 inline-flex items-center gap-1.5" data-testid="acc-expense-new">
              <Plus size={12}/> Nuevo gasto
            </button>
          </div>
        </div>

        {formOpen && <ExpenseForm period={period} onSaved={() => { setFormOpen(false); load(); }} onCancel={() => setFormOpen(false)}/>}

        <div className="panel overflow-hidden mt-3">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Categoría</th>
                <th className="text-left px-3 py-2">Proveedor / Descripción</th>
                <th className="text-right px-3 py-2">Neto</th>
                <th className="text-right px-3 py-2">IVA</th>
                <th className="text-right px-3 py-2">Retención</th>
                <th className="text-right px-3 py-2">Bruto</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-t border-white/5" data-testid={`acc-expense-${e.id}`}>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString("es-CL")}</td>
                  <td className="px-3 py-2 text-xs">{CAT_LABEL[e.category] || e.category}</td>
                  <td className="px-3 py-2 text-sm">
                    <div>{e.supplier_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{e.description}</div>
                    {e.document_number && <div className="text-[11px] text-muted-foreground">{e.document_kind} · {e.document_number}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{CLP(e.amount_net_clp)}</td>
                  <td className="px-3 py-2 text-right text-cyan-200">{CLP(e.iva_clp)}</td>
                  <td className="px-3 py-2 text-right text-amber-200">{CLP(e.retention_clp)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{CLP(e.amount_gross_clp)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(e.id)} className="text-muted-foreground hover:text-red-400" title="Eliminar" data-testid={`acc-expense-del-${e.id}`}>
                      <Trash2 size={13}/>
                    </button>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-sm">Sin gastos registrados en el período.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Transferencias a profesionales (Pago Protegido) */}
      <section className="mt-8" data-testid="acc-payouts-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="serif text-2xl inline-flex items-center gap-2">
            <Send size={18} className="text-cyan-300"/> Transferencias a profesionales
          </h2>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="text-sm text-muted-foreground">
              {payouts.count} transferencia{payouts.count === 1 ? "" : "s"} · Total <span className="text-cyan-300 font-semibold">{CLP(payouts.total_clp)}</span>
            </div>
            <button
              onClick={() => downloadCSV(
                `payouts-${period}.csv`,
                payouts.items || [],
                [
                  { key: "transfer_date", label: "Fecha" },
                  { key: "professional_name", label: "Profesional" },
                  { key: "professional_role", label: "Rol" },
                  { key: "professional_email", label: "Email cuenta" },
                  { key: "amount_clp", label: "Monto CLP" },
                  { key: "reference_type", label: "Referencia" },
                  { key: "reference_id", label: "Ref ID" },
                  { key: "notes", label: "Notas" },
                  { key: "authorized_by_email", label: "Autorizado por" },
                  { key: "at", label: "Autorizado en" },
                  { key: "email_sent", label: "Email enviado" },
                  { key: "banking_snapshot", label: "Banco", format: (v) => v?.bank || "" },
                  { key: "banking_snapshot", label: "Tipo cta", format: (v) => v?.account_type || "" },
                  { key: "banking_snapshot", label: "N° cuenta", format: (v) => v?.account_number || "" },
                  { key: "banking_snapshot", label: "RUT titular", format: (v) => v?.account_holder_rut || "" },
                ],
              )}
              disabled={(payouts.items || []).length === 0}
              className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="acc-payouts-export"
            ><Download size={12}/> Exportar CSV</button>
          </div>
        </div>

        {/* Resumen por profesional */}
        {payouts.by_professional && payouts.by_professional.length > 0 && (
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {payouts.by_professional.slice(0, 6).map((p) => (
              <Link
                key={p.professional_id}
                to="/app/admin/profesionales"
                className="panel p-3 hover:border-cyan-400/40 transition"
                data-testid={`acc-payouts-prof-${p.professional_id}`}
              >
                <div className="text-sm font-medium">{p.professional_name || "—"}</div>
                <div className="text-[10px] text-muted-foreground">{p.professional_role} · {p.count} transferencia{p.count === 1 ? "" : "s"}</div>
                <div className="text-cyan-300 font-semibold mt-1">{CLP(p.total_clp)}</div>
              </Link>
            ))}
          </div>
        )}

        <div className="panel overflow-hidden mt-3">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Profesional</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Cuenta bancaria</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Referencia</th>
                <th className="text-right px-3 py-2">Monto</th>
                <th className="text-center px-3 py-2 hidden md:table-cell">Email</th>
              </tr>
            </thead>
            <tbody>
              {(payouts.items || []).map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`acc-payout-row-${p.id}`}>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{p.transfer_date || "—"}</td>
                  <td className="px-3 py-2 text-sm">
                    <div>{p.professional_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{p.professional_email}</div>
                  </td>
                  <td className="px-3 py-2 text-xs hidden sm:table-cell">
                    {p.banking_snapshot ? (
                      <>
                        <div>{p.banking_snapshot.bank}</div>
                        <div className="text-[10px] text-muted-foreground">{p.banking_snapshot.account_type} · {p.banking_snapshot.account_number}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs hidden lg:table-cell">
                    <div>{p.reference_type}</div>
                    {p.reference_id && <div className="text-[10px] text-muted-foreground">{p.reference_id.slice(0, 12)}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-cyan-300">{CLP(p.amount_clp)}</td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    <span className={`pill text-[10px] ${p.email_sent ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"}`}>
                      {p.email_sent ? "Enviado" : "Falló"}
                    </span>
                  </td>
                </tr>
              ))}
              {(payouts.items || []).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-sm">Sin transferencias autorizadas en el período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  const c = {
    emerald: { border: "border-emerald-400/40", text: "text-emerald-300" },
    amber:   { border: "border-amber-400/40",   text: "text-amber-300"   },
    cyan:    { border: "border-cyan-400/40",    text: "text-cyan-300"    },
  }[accent] || { border: "border-white/10", text: "text-muted-foreground" };
  return (
    <div className={`panel p-4 ${c.border}`} data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 text-xs ${c.text} font-medium`}>
        <Icon size={13}/> {label}
      </div>
      <div className="serif text-3xl mt-2">{value}</div>
    </div>
  );
}

function ExpenseForm({ period, onSaved, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [category, setCategory] = useState("software_licencias");
  const [supplier, setSupplier] = useState("");
  const [rut, setRut] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [hasIva, setHasIva] = useState(true);
  const [hasRet, setHasRet] = useState(false);
  const [docKind, setDocKind] = useState("factura");
  const [docNumber, setDocNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await api.post("/admin/accounting/expenses", {
        date: new Date(date).toISOString(),
        category,
        supplier_name: supplier || null,
        supplier_rut: rut || null,
        description,
        amount_net_clp: parseInt(String(amount).replace(/[^0-9]/g, ""), 10),
        has_iva: hasIva,
        has_retention: hasRet,
        document_kind: docKind,
        document_number: docNumber || null,
      });
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="panel p-5 mt-3 space-y-3" data-testid="acc-expense-form">
      <div className="grid md:grid-cols-4 gap-3">
        <div>
          <label className="label-eyebrow">Fecha</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field mt-1" required/>
        </div>
        <div>
          <label className="label-eyebrow">Categoría</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="field mt-1">
            {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow">Proveedor</label>
          <input value={supplier} onChange={(e) => setSupplier(e.target.value)} className="field mt-1"/>
        </div>
        <div>
          <label className="label-eyebrow">RUT proveedor</label>
          <input value={rut} onChange={(e) => setRut(e.target.value)} className="field mt-1" placeholder="76.123.456-K"/>
        </div>
      </div>
      <div>
        <label className="label-eyebrow">Descripción</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="field mt-1" required minLength={3} maxLength={280} data-testid="acc-expense-description"/>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <div>
          <label className="label-eyebrow">Monto neto CLP</label>
          <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))} className="field mt-1" required data-testid="acc-expense-amount"/>
        </div>
        <div>
          <label className="label-eyebrow">Documento</label>
          <select value={docKind} onChange={(e) => setDocKind(e.target.value)} className="field mt-1">
            <option value="factura">Factura</option>
            <option value="boleta_honorarios">Boleta de honorarios</option>
            <option value="recibo">Recibo</option>
            <option value="otro">Otro</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow">N° documento</label>
          <input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} className="field mt-1"/>
        </div>
        <div className="flex items-end gap-3 pb-1">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={hasIva} onChange={(e) => setHasIva(e.target.checked)}/> Con IVA (19%)
          </label>
        </div>
      </div>
      <label className="text-sm inline-flex items-center gap-2">
        <input type="checkbox" checked={hasRet} onChange={(e) => setHasRet(e.target.checked)}/>
        Aplicar retención honorarios (13,75-14,5% para boletas)
      </label>
      {err && <div className="text-sm text-[hsl(var(--destructive))]">{err}</div>}
      <div className="flex items-center gap-2 pt-2">
        <button type="submit" disabled={saving} className="btn-primary text-sm py-1.5 px-3 disabled:opacity-50" data-testid="acc-expense-save">
          {saving ? "Guardando…" : "Registrar gasto"}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-sm py-1.5 px-3">Cancelar</button>
      </div>
    </form>
  );
}
